import React from "react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Home, LogOut } from "lucide-react";

/**
 * ErrorBoundary global.
 *
 * Captura qualquer erro de runtime e mostra uma tela AMIGÁVEL ao usuário —
 * sem expor stack trace ou mensagens técnicas em vermelho.
 *
 * Acoes oferecidas:
 *  - Recarregar aplicativo (limpa caches e service workers antes)
 *  - Voltar para o início
 *  - Sair e fazer login novamente
 *
 * O erro técnico ainda é registrado no console e em localStorage para
 * diagnóstico posterior pela equipe técnica (sem incomodar o aluno).
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorMessage: (error && (error.message || String(error))) || "",
    };
  }

  componentDidCatch(error, info) {
    try {
      console.error("[CEAP] ErrorBoundary capturou:", error);
      console.error("[CEAP] Stack do componente:", info?.componentStack);
      const stored = JSON.parse(localStorage.getItem("ceap_errors") || "[]");
      stored.unshift({
        at: new Date().toISOString(),
        message: error?.message || String(error),
        stack: (error?.stack || "").slice(0, 1500),
        component: (info?.componentStack || "").slice(0, 1500),
        url: window.location.href,
        ua: navigator.userAgent,
      });
      localStorage.setItem("ceap_errors", JSON.stringify(stored.slice(0, 5)));
    } catch {
      /* silencioso */
    }
  }

  hardReload = () => {
    try {
      if ("caches" in window) {
        caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
      }
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker
          .getRegistrations()
          .then((regs) => regs.forEach((r) => r.unregister()))
          .catch(() => {});
      }
    } catch {
      /* silencioso */
    }
    setTimeout(() => window.location.reload(), 200);
  };

  goHome = () => {
    this.setState({ hasError: false, errorMessage: "" });
    window.location.href = "/home";
  };

  logoutAndLogin = () => {
    try {
      localStorage.removeItem("ceap_token");
      localStorage.removeItem("ceap_user");
    } catch {
      /* silencioso */
    }
    window.location.href = "/login";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-hero-radial bg-white flex items-center justify-center p-6">
        <div className="max-w-[440px] w-full bg-white rounded-2xl shadow-lg border border-[hsl(var(--border))] p-7 text-center">
          <div className="flex justify-center mb-3">
            <Logo size={36} />
          </div>
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mt-2 mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="font-display text-xl font-semibold text-[hsl(var(--primary))]">
            Ops, algo deu errado
          </h1>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            Tivemos um problema momentâneo ao carregar essa tela. <strong>Seus dados estão seguros.</strong>
            <br />
            Recarregue o aplicativo para continuar.
          </p>

          <div className="mt-5 space-y-2">
            <Button
              onClick={this.hardReload}
              className="w-full h-11"
              data-testid="error-reload-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Recarregar aplicativo
            </Button>
            <Button
              onClick={this.goHome}
              variant="outline"
              className="w-full h-11"
              data-testid="error-home-btn"
            >
              <Home className="w-4 h-4 mr-2" />
              Voltar para o início
            </Button>
            <button
              type="button"
              onClick={this.logoutAndLogin}
              className="w-full h-9 text-xs text-muted-foreground hover:text-[hsl(var(--primary))] hover:underline flex items-center justify-center gap-1.5"
              data-testid="error-logout-btn"
            >
              <LogOut className="w-3 h-3" />
              Sair e fazer login novamente
            </button>
          </div>

          <p className="text-[10px] text-muted-foreground mt-5 leading-relaxed">
            Se o problema persistir, avise a equipe do CEAP informando o horário em que ocorreu.
          </p>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
